/**
 * Grab anything you built — hands or controllers, near or far.
 *
 *   Hands        reach into an object and pinch, or point at it and pinch-hold;
 *                it follows your hand until you let go. Twist your wrist (roll
 *                it about the way your hand points, like a door knob) to turn
 *                it; sweeping your arm moves it without spinning it.
 *   Controllers  squeeze the grip button — on an object up close, or with the
 *                ray on one far away. Twist the controller to turn it. While
 *                holding: right stick up / down pushes it away / pulls it in,
 *                left / right spins it.
 *
 * Palette ▸ Edit picks what a grab does, the same for hands and controllers,
 * shapes and models:
 *   Move  carry it (above)
 *   Turn  drag round the object: it turns about its vertical axis by the angle
 *         your grab point goes round it — works whichever way you face
 *   Size  drag away from / towards its middle: it grows / shrinks, keeping its
 *         proportions (a wall stays a wall)
 *
 * What's grabbable lights up (teal; gold while held). Shapes and models;
 * locked items stay put. Grabbing also selects. On release the change is
 * snapped (grid + 15°, or 10 % size steps) when Snap is on, then saved and
 * broadcast like any other edit (so it's undoable and everyone sees it) — only
 * if something actually changed. A second hand pinching on the object hands
 * it over to the two-hand gesture (VRHandManipulator).
 *
 * The trigger never grabs — it's for pressing buttons, selecting and dropping
 * held items, so grabbing and menus never fight. Hand pinches go through the
 * shared pinch filter (hands/pinch.ts): a tracking blip doesn't drop anything,
 * and the pinch that drops a held item can't also start a grab.
 *
 * Resize handles: a selected shape shows a ball on each face (red X, green Y,
 * blue Z). Grab one — near or along the ray — and pull: that side moves, the
 * opposite face stays put. Snap rounds the size to 5 cm. A selected model
 * shows one gold ball on its top corner: pull it out / in to resize the whole
 * model.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore } from '../../../stores/worldStore';
import { broadcastObjectUpdate } from '../../../services/worldBroadcast';
import { hands, handMode, pinch, type HandSide } from '../hands/handTracking';
import { PINCH_SETTLE_MS } from '../hands/pinch';
import { useVRBuild } from './vrBuildState';
import { rayHitsUi } from './vrUiRay';
import {
  TwistTracker, OneEuro, OneEuro3, grabTarget, sizeFactor, proportionalScale, turnAngle, snapK, objectBox, SCALE_MIN, SCALE_MAX,
} from './vrBuildMath';

type Kind = 'primitive' | 'content';
type Op = 'move' | 'turn' | 'size';
interface Target { id: string; kind: Kind; obj: THREE.Object3D }
interface Grabber {
  side: HandSide; pos: THREE.Vector3; ray: THREE.Vector3; dir: THREE.Vector3;
  /** Which way the hand / controller points, and a vector across it that rolls with it (for the twist). */
  fwd: THREE.Vector3; up: THREE.Vector3;
  pressed: boolean; free: boolean; pad: Gamepad | null; isHand: boolean;
}
/** axis -1 = the uniform corner handle on a model. */
interface Handle { axis: -1 | 0 | 1 | 2; sign: 1 | -1; pos: THREE.Vector3; dir: THREE.Vector3; r: number }

const NEAR_PAD = .04;  // how close (m) a grab point must be to count as "inside"
const FAR = 12;        // longest ray grab (m)
const STICK = .25;     // thumbstick dead zone
const ROT_STEP = Math.PI / 12;
const SIZE_STEP = .05;
const AXIS_COLOR = ['#ff5a6e', '#5be37d', '#4d9dff'];
const UNIFORM_COLOR = '#ffd166';
const NO_HANDLES = new Set(['gametable', 'arcade', 'leaderboard', 'portal', 'text', 'npc']);

const pulse = (pad: Gamepad | null | undefined, v = .35, ms = 30) => {
  try { (pad as any)?.hapticActuators?.[0]?.pulse?.(v, ms); } catch { /* no haptics */ }
};
const opFor = (mode: string): Op => mode === 'rotate' ? 'turn' : mode === 'scale-uniform' || mode === 'scale' ? 'size' : 'move';
const newGrabber = (side: HandSide): Grabber => ({
  side, pos: new THREE.Vector3(), ray: new THREE.Vector3(), dir: new THREE.Vector3(0, 0, -1),
  fwd: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0), pressed: false, free: false, pad: null, isHand: false,
});

export default function VRGrab({ worldId }: { worldId: string }) {
  const { scene, gl } = useThree();
  // Shapes skip their own ray-drag while this owns grabbing (all modes).
  useEffect(() => { (window as any).__vrGrabOn = true; return () => { (window as any).__vrGrabOn = false; }; }, []);
  const origin = useXR((s) => s.origin);
  const session = useXR((s) => s.session);
  const canBuild = useWorldStore((s) => s.canBuild);

  const targets = useRef<Target[]>([]);
  const targetObjs = useRef<THREE.Object3D[]>([]);
  const lastScan = useRef(0);
  const prev = useRef<Record<HandSide, boolean>>({ left: true, right: true });
  const hover = useRef<Target | null>(null);

  // All per-grab state is allocated once and reused.
  const G = useMemo(() => ({
    t: null as Target | null, side: 'right' as HandSide, isHand: false, dist: 0, near: false, op: 'move' as Op,
    offset: new THREE.Vector3(), objYaw0: 0, stickYaw: 0, start: new THREE.Vector3(), moved: false,
    twist: new TwistTracker(), smooth: new OneEuro3(1.5, 4), yawSmooth: new OneEuro(1, .4),
    center: new THREE.Vector3(), p0: new THREE.Vector3(), s0: new THREE.Vector3(), lastTurn: 0, minR: .15, factor: 1,
    settleUntil: 0,
  }), []);
  const grab = useRef<typeof G | null>(null);
  const R = useMemo(() => ({
    t: null as Target | null, side: 'right' as HandSide, near: false, dist: 0, axis: 0 as -1 | 0 | 1 | 2, sign: 1,
    axisW: new THREE.Vector3(), size0: 0, center0: new THREE.Vector3(), p0: new THREE.Vector3(), moved: false,
    s0: 1, c: new THREE.Vector3(), d0: 1, factor: 1, settleUntil: 0,
  }), []);
  const resize = useRef<typeof R | null>(null);
  const handles = useMemo<Handle[]>(() => Array.from({ length: 6 }, () => ({ axis: 0, sign: 1, pos: new THREE.Vector3(), dir: new THREE.Vector3(), r: .03 })), []);
  const handleCount = useRef(0);
  const handleMeshes = useRef<(THREE.Mesh | null)[]>([]);
  const hotHandle = useRef(-1);

  const box = useMemo(() => {
    const helper = new THREE.Box3Helper(new THREE.Box3(), new THREE.Color('#5ee6dc'));
    (helper.material as THREE.LineBasicMaterial).depthTest = false;
    (helper.material as THREE.LineBasicMaterial).transparent = true;
    helper.renderOrder = 998;
    helper.raycast = () => {};
    helper.visible = false;
    return helper;
  }, []);
  const v = useMemo(() => ({
    m: new THREE.Matrix4(), q: new THREE.Quaternion(), w: new THREE.Vector3(), b: new THREE.Box3(),
    ray: new THREE.Raycaster(), tmp: new THREE.Vector3(), head: new THREE.Vector3(), gp: new THREE.Vector3(),
    c: new THREE.Vector3(), oq: new THREE.Quaternion(), sc: new THREE.Vector3(), a: new THREE.Vector3(),
  }), []);
  const pool = useMemo(() => ({ left: newGrabber('left'), right: newGrabber('right') }), []);
  const gs = useMemo<Grabber[]>(() => [], []);
  // Reused results (no per-frame allocation).
  const pickRes = useMemo(() => ({ t: null as unknown as Target, dist: 0, near: false }), []);
  const handleRes = useMemo(() => ({ i: -1, near: false, dist: 0 }), []);
  const bySide = (s: HandSide) => { for (const g of gs) if (g.side === s) return g; return undefined; };

  /** Everything grabbable right now (refreshed twice a second). */
  const scan = () => {
    const st = useWorldStore.getState();
    const out: Target[] = [];
    scene.traverse((o) => {
      if (!o.visible) return;
      if (o.name.startsWith('primitive-')) {
        const id = o.name.slice('primitive-'.length);
        const p = st.primitives[id];
        if (p && !p.isLocked) out.push({ id, kind: 'primitive', obj: o });
      } else if (o.userData?.objectId && st.contentItems[o.userData.objectId]) {
        const id = o.userData.objectId as string;
        if (!(st.contentItems[id] as any).isLocked && !out.some((t) => t.id === id)) out.push({ id, kind: 'content', obj: o });
      }
    });
    targets.current = out;
    targetObjs.current = out.map((t) => t.obj);
  };

  /** The right / left grabber this frame (hand pinch point or controller grip), into reused objects. */
  const grabbers = (frame?: XRFrame): Grabber[] => {
    gs.length = 0;
    if (!origin || !session) return gs;
    const mw = origin.matrixWorld;
    if (handMode.active) {
      for (const side of ['right', 'left'] as HandSide[]) {
        const h = hands[side];
        if (!h.present) continue;
        const g = pool[side];
        g.pos.copy(h.indexTip).add(h.thumbTip).multiplyScalar(.5).applyMatrix4(mw);
        g.dir.copy(h.rayDir).transformDirection(mw);
        g.ray.copy(h.rayOrigin).applyMatrix4(mw);
        // Hand frame from the joints: wrist → middle knuckle points the way the hand does; the palm normal rolls with it.
        g.fwd.copy(h.palm).sub(h.wrist).transformDirection(mw);
        g.up.copy(h.palmNormal).transformDirection(mw);
        g.pressed = pinch[side].held; g.free = pinch[side].free; g.pad = null; g.isHand = true;
        gs.push(g);
      }
      return gs;
    }
    const ref = gl.xr.getReferenceSpace();
    if (!frame || !ref) return gs;
    for (const src of session.inputSources) {
      if (src.hand || !src.gripSpace || (src.handedness !== 'left' && src.handedness !== 'right')) continue;
      const pose = frame.getPose(src.gripSpace, ref);
      if (!pose) continue;
      const g = pool[src.handedness];
      v.m.fromArray(pose.transform.matrix).premultiply(mw);
      g.pos.setFromMatrixPosition(v.m);
      // Aim with the pointer ray (what the laser shows), not the grip's angle.
      const rp = frame.getPose(src.targetRaySpace, ref) ?? pose;
      v.m.fromArray(rp.transform.matrix).premultiply(mw);
      g.ray.setFromMatrixPosition(v.m);
      v.q.setFromRotationMatrix(v.m);
      g.dir.set(0, 0, -1).applyQuaternion(v.q);
      g.fwd.copy(g.dir);
      g.up.set(0, 1, 0).applyQuaternion(v.q);
      g.pressed = !!src.gamepad?.buttons?.[1]?.pressed; g.free = g.pressed; g.pad = src.gamepad ?? null; g.isHand = false;
      gs.push(g);
    }
    return gs;
  };

  /** Near first (grab point inside an object's box), then along the ray. */
  const pick = (g: Grabber): { t: Target; dist: number; near: boolean } | null => {
    let best: Target | null = null; let bestVol = Infinity;
    for (const t of targets.current) {
      objectBox(t.obj, v.b).expandByScalar(NEAR_PAD);
      if (!v.b.containsPoint(g.pos)) continue;
      const s = v.b.getSize(v.tmp); const vol = s.x * s.y * s.z;
      if (vol < bestVol) { bestVol = vol; best = t; } // the smallest one you're inside
    }
    if (best) { pickRes.t = best; pickRes.dist = best.obj.getWorldPosition(v.w).distanceTo(g.pos); pickRes.near = true; return pickRes; }
    v.ray.set(g.ray, g.dir); v.ray.far = FAR;
    const objs = targetObjs.current;
    const hit = v.ray.intersectObjects(objs, true)[0];
    if (!hit) return null;
    let o: THREE.Object3D | null = hit.object;
    while (o && !objs.includes(o)) o = o.parent;
    const t = targets.current.find((x) => x.obj === o);
    if (!t) return null;
    pickRes.t = t; pickRes.dist = hit.distance; pickRes.near = false;
    return pickRes;
  };

  /** Handles for the selection: six face balls on a shape, one corner ball on a model (none otherwise). */
  const updateHandles = (head: THREE.Vector3): Target | null => {
    handleCount.current = 0;
    const st = useWorldStore.getState();
    const sel = st.selectedObjectId;
    if (!sel) return null;
    const p = st.primitives[sel];
    const c = st.contentItems[sel] as any;
    if (p ? (p.isLocked || NO_HANDLES.has(p.shape)) : (!c || c.isLocked)) return null;
    const t = targets.current.find((x) => x.id === sel);
    if (!t) return null;
    const o = t.obj; o.updateWorldMatrix(true, false);
    if (t.kind === 'content') {
      // One uniform handle on the top corner of the model's box that faces you.
      objectBox(o, v.b);
      if (v.b.isEmpty()) return null;
      const h = handles[0];
      v.b.getCenter(v.c);
      h.pos.set(head.x > v.c.x ? v.b.max.x : v.b.min.x, v.b.max.y, head.z > v.c.z ? v.b.max.z : v.b.min.z);
      h.dir.copy(h.pos).sub(v.c).normalize();
      h.pos.addScaledVector(h.dir, .04);
      h.axis = -1; h.sign = 1;
      h.r = .026 + .012 * h.pos.distanceTo(head);
      handleCount.current = 1;
      return t;
    }
    o.getWorldPosition(v.c);
    o.getWorldQuaternion(v.oq);
    o.getWorldScale(v.sc);
    const ext = [Math.abs(v.sc.x), Math.abs(v.sc.y), Math.abs(v.sc.z)];
    let i = 0;
    for (const axis of [0, 1, 2] as const) for (const sign of [1, -1] as const) {
      const h = handles[i++];
      h.axis = axis; h.sign = sign;
      h.dir.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0).applyQuaternion(v.oq).multiplyScalar(sign);
      h.pos.copy(v.c).addScaledVector(h.dir, ext[axis] / 2 + .05);
      // Bigger the further away it is, so it stays easy to hit.
      h.r = .022 + .012 * h.pos.distanceTo(head);
    }
    handleCount.current = 6;
    return t;
  };

  const pickHandle = (g: Grabber): { i: number; near: boolean; dist: number } | null => {
    let best = -1; let bestD = Infinity;
    for (let i = 0; i < handleCount.current; i++) {
      const d = g.pos.distanceTo(handles[i].pos);
      if (d < handles[i].r * 1.6 && d < bestD) { best = i; bestD = d; }
    }
    if (best >= 0) { handleRes.i = best; handleRes.near = true; handleRes.dist = 0; return handleRes; }
    for (let i = 0; i < handleCount.current; i++) {
      const h = handles[i];
      const along = v.tmp.copy(h.pos).sub(g.ray).dot(g.dir);
      if (along <= 0 || along > FAR) continue;
      const off = v.w.copy(g.ray).addScaledVector(g.dir, along).distanceTo(h.pos);
      if (off < h.r && along < bestD) { best = i; bestD = along; }
    }
    if (best < 0) return null;
    handleRes.i = best; handleRes.near = false; handleRes.dist = bestD;
    return handleRes;
  };

  const v3 = (p: THREE.Vector3): [number, number, number] => [+p.x.toFixed(3), +p.y.toFixed(3), +p.z.toFixed(3)];

  const commitResize = (t: Target) => {
    const o = t.obj;
    const st = useWorldStore.getState();
    if (t.kind === 'content') {
      const scale = +o.scale.x.toFixed(4);
      st.updateContentItem(t.id, { scale } as any);
      if (worldId) broadcastObjectUpdate(worldId, 'content', t.id, { scale });
      return;
    }
    const position = v3(o.position);
    const scale = v3(o.scale);
    st.updatePrimitive(t.id, { position, scale });
    if (worldId) broadcastObjectUpdate(worldId, 'primitive', t.id, { position, scale });
  };

  /** Save a move (or, with `turnOnly`, a Turn-mode turn — its position isn't re-snapped). */
  const commit = (t: Target, turnOnly = false) => {
    const st = useWorldStore.getState();
    const snap = st.snapSettings;
    const o = t.obj;
    if (snap.grid) {
      const gs = snap.gridSize || .5;
      if (!turnOnly) o.position.set(Math.round(o.position.x / gs) * gs, Math.max(0, Math.round(o.position.y / gs) * gs), Math.round(o.position.z / gs) * gs);
      o.rotation.y = Math.round(o.rotation.y / ROT_STEP) * ROT_STEP;
    }
    const position = v3(o.position);
    const rotation: [number, number, number] = [o.rotation.x, o.rotation.y, o.rotation.z];
    if (t.kind === 'primitive') {
      st.updatePrimitive(t.id, { position, rotation });
      if (worldId) broadcastObjectUpdate(worldId, 'primitive', t.id, { position, rotation });
    } else {
      st.updateContentItem(t.id, { position, rotation } as any);
      if (worldId) broadcastObjectUpdate(worldId, 'content', t.id, { position, rotation });
    }
  };

  /** Size mode: proportional scale from the grab-time scale. */
  const applySize = (o: THREE.Object3D, kind: Kind, s0: THREE.Vector3, f: number) => {
    if (kind === 'content') o.scale.setScalar(THREE.MathUtils.clamp(s0.x * f, .005, SCALE_MAX));
    else proportionalScale(s0, f, o.scale, SCALE_MIN, SCALE_MAX);
  };

  const release = (commitIt: boolean) => {
    const g = grab.current; if (!g || !g.t) return;
    grab.current = null;
    if (commitIt) {
      if (g.op === 'size') {
        if (useWorldStore.getState().snapSettings.grid) applySize(g.t.obj, g.t.kind, g.s0, snapK(g.factor));
        commitResize(g.t);
      } else commit(g.t, g.op === 'turn');
    }
    useVRBuild.getState().setHandling(false);
    useVRBuild.getState().setHandlingSide(null);
    useVRBuild.getState().setGrabbedId(null);
    (window as any).__vrGrabbing = false;
  };

  const showBox = (o: THREE.Object3D, color: string) => {
    objectBox(o, v.b); box.box.copy(v.b); (box.material as THREE.LineBasicMaterial).color.set(color); box.visible = true;
  };

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as any).__gxGrab = { pool, grab, resize, handles, handleCount, hotHandle, hover };
    return () => { delete (window as any).__gxGrab; };
  }, [pool, handles]);

  useFrame((state, dt, frame?: XRFrame) => {
    if (!session || !origin || !canBuild) { box.visible = false; handleMeshes.current.forEach((m) => { if (m) m.visible = false; }); return; }
    const build = useVRBuild.getState();
    origin.updateMatrixWorld();
    const now = performance.now();
    if (now - lastScan.current > 500) { scan(); lastScan.current = now; }
    grabbers(frame);

    // Something in your hand (palette placement) or a ray placement owns the press.
    const placing = !!build.held || !!build.placing || useWorldStore.getState().activeTool === 'place';
    const mode = (window as any).__vrTransformMode || 'translate';

    // ---- Resize handles ----
    const head = v.head.setFromMatrixPosition(state.camera.matrixWorld);
    const rz = resize.current;
    if (rz && rz.t) {
      const g = bySide(rz.side);
      if (pinch[rz.side].held && (!g || pinch[rz.side].releasing)) {
        // Hand lost for a moment, or the pinch is in its release grace (fingers
        // opening, a tracking blip): hold still — opening the fingers shifts the pinch point.
      } else if (!g || !g.pressed) {
        resize.current = null;
        if (rz.moved) commitResize(rz.t);
        build.setHandling(false);
        build.setHandlingSide(null);
        build.setResizing(false);
        if (g) prev.current[g.side] = g.pressed;
      } else {
        const p = rz.near ? v.gp.copy(g.pos) : v.gp.copy(g.ray).addScaledVector(g.dir, rz.dist);
        const o = rz.t.obj;
        if (now < rz.settleUntil) {
          // Fingers still closing: re-take the start point instead of resizing.
          rz.p0.copy(p);
          if (rz.axis === -1) rz.d0 = Math.max(.05, p.distanceTo(rz.c));
        } else if (rz.axis === -1) {
          // Uniform: distance from the model's middle now vs at the start.
          let f = Math.max(.02, p.distanceTo(rz.c) / rz.d0);
          if (useWorldStore.getState().snapSettings.grid) f = snapK(f);
          rz.factor = f;
          o.scale.setScalar(THREE.MathUtils.clamp(rz.s0 * f, .005, SCALE_MAX));
          if (Math.abs(f - 1) > .01) rz.moved = true;
        } else {
          let size = rz.size0 + v.tmp.copy(p).sub(rz.p0).dot(rz.axisW);
          if (useWorldStore.getState().snapSettings.grid) size = Math.round(size / SIZE_STEP) * SIZE_STEP;
          size = Math.min(60, Math.max(.05, size));
          if (rz.axis === 0) o.scale.x = size; else if (rz.axis === 1) o.scale.y = size; else o.scale.z = size;
          v.c.copy(rz.center0).addScaledVector(rz.axisW, (size - rz.size0) / 2);
          if (o.parent) o.parent.worldToLocal(v.c);
          o.position.copy(v.c);
          if (Math.abs(size - rz.size0) > .005) rz.moved = true;
        }
        showBox(o, '#ffd166');
      }
    }
    const handleOwner = !grab.current && !placing && !build.twoHand ? updateHandles(head) : (handleCount.current = 0, null);
    hotHandle.current = -1;
    if (handleOwner && !resize.current && !build.handling) {
      for (const g of gs) {
        const h = pickHandle(g);
        if (!h) continue;
        // Priority: a handle you touch > an object you're inside > a handle you point at.
        if (!h.near && pick(g)?.near) continue;
        if (hotHandle.current < 0) hotHandle.current = h.i;
        // Either hand / controller may start a resize — the first one to press.
        if (g.pressed && !prev.current[g.side] && g.free) {
          hotHandle.current = h.i;
          const hd = handles[h.i]; const o = handleOwner.obj;
          R.t = handleOwner; R.side = g.side; R.near = h.near; R.dist = h.dist; R.axis = hd.axis; R.sign = hd.sign;
          R.axisW.copy(hd.dir); R.moved = false; R.factor = 1; R.settleUntil = g.isHand ? now + PINCH_SETTLE_MS : 0;
          R.size0 = hd.axis === -1 ? 0 : Math.abs(hd.axis === 0 ? o.scale.x : hd.axis === 1 ? o.scale.y : o.scale.z);
          o.getWorldPosition(R.center0);
          if (h.near) R.p0.copy(g.pos); else R.p0.copy(g.ray).addScaledVector(g.dir, h.dist);
          if (hd.axis === -1) {
            objectBox(o, v.b); v.b.getCenter(R.c);
            R.d0 = Math.max(.05, R.p0.distanceTo(R.c));
            R.s0 = o.scale.x;
          }
          resize.current = R;
          build.setHandlingSide(g.side);
          build.setHandling(true);
          build.setResizing(true);
          pulse(g.pad);
          break;
        }
      }
    }
    handleMeshes.current.forEach((m, i) => {
      if (!m) return;
      const hd = i < handleCount.current ? handles[i] : null;
      const r = resize.current;
      const active = !!r && !!hd && r.axis === hd.axis && Math.sign(r.sign) === hd.sign;
      m.visible = !!hd && (!r || active);
      if (!hd) return;
      m.position.copy(hd.pos);
      m.scale.setScalar(hd.r * (i === hotHandle.current || active ? 1.35 : 1));
      (m.material as THREE.MeshBasicMaterial).color.set(i === hotHandle.current || active ? '#ffffff' : hd.axis === -1 ? UNIFORM_COLOR : AXIS_COLOR[hd.axis]);
    });
    if (resize.current || hotHandle.current >= 0) {
      for (const g of gs) prev.current[g.side] = g.pressed;
      if (!resize.current) box.visible = false;
      return;
    }

    // ---- Holding: follow the grabber ----
    const cur = grab.current;
    if (cur && cur.t) {
      const g = bySide(cur.side);
      if (build.twoHand) {
        // The two-hand gesture took over: it saves the whole change (incl. this hand's move) when it ends.
        grab.current = null;
        build.setGrabbedId(null); // (__vrGrabbing stays set: the two-hand gesture owns it now)
      } else if (cur.isHand && pinch[cur.side].held && (!g || pinch[cur.side].releasing)) {
        // The hand dropped out of tracking for a frame or two, or the pinch is in
        // its release grace (fingers opening / a blip): keep holding, but still —
        // opening the fingers moves the pinch point, which would nudge the object as you let go.
        showBox(cur.t.obj, '#ffd166');
        return;
      } else if (!g || !g.pressed) { release(cur.moved); prev.current[cur.side] = !!g?.pressed; }
      else {
        const o = cur.t.obj;
        // Grab point: the pinch / grip up close, or the point along the ray it was grabbed at — smoothed.
        if (cur.near) v.gp.copy(g.pos); else v.gp.copy(g.ray).addScaledVector(g.dir, cur.dist);
        if (now < cur.settleUntil) {
          // Fingers still closing (the pinch point drifts a few cm): re-take the grab pose, don't move it yet.
          o.getWorldPosition(v.w);
          cur.offset.copy(v.w).sub(v.gp);
          cur.p0.copy(v.gp);
          cur.smooth.reset(v.gp);
          cur.twist.reset(g.fwd, g.up);
          cur.yawSmooth.reset(0);
          showBox(o, '#ffd166');
          prev.current[cur.side] = true;
          return;
        }
        const gp = cur.smooth.filter(v.gp, dt);
        if (cur.op === 'move') {
          // Thumbstick (controllers): push / pull along the ray, spin.
          if (g.pad) {
            const ax = g.pad.axes?.[2] ?? 0, ay = g.pad.axes?.[3] ?? 0;
            if (Math.abs(ay) > STICK) cur.dist = Math.min(FAR, Math.max(.15, cur.dist - ay * 2.5 * dt));
            if (Math.abs(ax) > STICK) cur.stickYaw -= ax * 2.2 * dt;
          }
          // Turn only by twisting (not by where the ray points), smoothed.
          const yaw = cur.yawSmooth.filter(cur.twist.update(g.fwd, g.up), dt) + cur.stickYaw;
          grabTarget(gp, cur.offset, yaw, v.w);
          if (o.parent) o.parent.worldToLocal(v.w);
          o.position.copy(v.w);
          o.rotation.y = cur.objYaw0 + yaw;
          if (!cur.moved && (o.position.distanceTo(cur.start) > .01 || Math.abs(yaw) > .02)) cur.moved = true;
        } else if (cur.op === 'turn') {
          const a = turnAngle(cur.center, cur.p0, gp);
          if (a !== null) cur.lastTurn = a;
          o.rotation.y = cur.objYaw0 + cur.lastTurn;
          if (!cur.moved && Math.abs(cur.lastTurn) > .02) cur.moved = true;
        } else {
          cur.factor = sizeFactor(cur.center, cur.p0, gp, cur.minR);
          applySize(o, cur.t.kind, cur.s0, cur.factor);
          if (!cur.moved && Math.abs(cur.factor - 1) > .01) cur.moved = true;
        }
        showBox(o, '#ffd166');
        prev.current[cur.side] = true;
        return;
      }
    }

    // ---- Not holding: hover + start a grab ----
    hover.current = null;
    if (!placing && !build.handling && !build.twoHand) {
      for (const g of gs) {
        const p = pick(g);
        if (!p) continue;
        if (!p.near && rayHitsUi(v.ray, scene, g.ray, g.dir, p.dist)) continue;
        if (!hover.current) hover.current = p.t;
        // A new press that no other tool has used (e.g. the pinch that just dropped a held item).
        if (g.pressed && !prev.current[g.side] && g.free) {
          const o = p.t.obj;
          o.getWorldPosition(v.w);
          const c = G;
          c.t = p.t; c.side = g.side; c.isHand = g.isHand; c.near = p.near; c.dist = p.near ? 0 : p.dist;
          c.op = opFor(mode); c.moved = false; c.stickYaw = 0; c.lastTurn = 0; c.factor = 1;
          c.settleUntil = g.isHand ? now + PINCH_SETTLE_MS : 0;
          // Keep where you grabbed it: offset from the grab point to the object's origin.
          if (p.near) v.gp.copy(g.pos); else v.gp.copy(g.ray).addScaledVector(g.dir, c.dist);
          c.offset.copy(v.w).sub(v.gp);
          c.p0.copy(v.gp);
          c.center.copy(v.w);
          c.objYaw0 = o.rotation.y;
          c.start.copy(o.position);
          c.s0.copy(o.scale);
          objectBox(o, v.b); v.b.getSize(v.a);
          c.minR = Math.max(.15, .25 * Math.max(v.a.x, v.a.y, v.a.z));
          c.twist.reset(g.fwd, g.up);
          c.smooth.reset(v.gp);
          c.yawSmooth.reset(0);
          grab.current = c;
          useWorldStore.getState().setSelectedObjectId(p.t.id);
          build.setGrabbedId(p.t.id);
          build.setHandlingSide(g.side);
          build.setHandling(true);
          (window as any).__vrGrabbing = true;
          pulse(g.pad);
          hover.current = p.t;
          break; // one grab at a time — but any hand / controller may be the one (no first-hover-wins)
        }
      }
    }
    for (const g of gs) prev.current[g.side] = g.pressed;
    if (hover.current) showBox(hover.current.obj, '#5ee6dc');
    else box.visible = false;
  });

  if (!session || !canBuild) return null;
  return <>
    <primitive object={box} />
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <mesh key={i} ref={(m) => { handleMeshes.current[i] = m; }} visible={false} renderOrder={999} raycast={() => {}} name="vr-resize-handle">
        <sphereGeometry args={[1, 16, 12]} />
        <meshBasicMaterial color={AXIS_COLOR[Math.floor(i / 2)]} depthTest={false} transparent opacity={.95} toneMapped={false} />
      </mesh>
    ))}
  </>;
}
